import React, { FC, useMemo } from 'react';
import styles from '../journey-explorer.module.scss';
import { JourneyExploreResult, JourneySimplify } from '../../../../models/journey-explorer/journey-explorer';
import { formatCompact, formatDuration } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import { isGroupLabel, viewName } from '../journey-simplify';
import { buildFunnel, dropOffs, frictionSteps, mainRoute } from '../journey-funnel';
import { Dot } from './filter-group';

interface JourneyFunnelViewProps {
	result: JourneyExploreResult | null;
	lookup: Map<string, InteractionInfo>;
	simplify: JourneySimplify;
	goal: string | null;
	goalOptions: string[];
	loading: boolean;
	onGoalChange: (goal: string | null) => void;
	onStoppedHere: (name: string) => void;
}

const pctText = (value: number) => `${value < 10 ? value.toFixed(1) : value.toFixed(0)}%`;

const JourneyFunnelView: FC<JourneyFunnelViewProps> = ({
	result,
	lookup,
	simplify,
	goal,
	goalOptions,
	loading,
	onGoalChange,
	onStoppedHere,
}) => {
	const goalView = goal ? viewName(simplify, goal) : null;
	const interactions = useMemo(() => result?.interactions ?? [], [result]);
	const funnel = useMemo(
		() => (result ? buildFunnel(result, mainRoute(result.interactions, result.transitions, goalView)) : []),
		[result, goalView]
	);
	const losses = useMemo(() => dropOffs(interactions, goalView), [interactions, goalView]);
	const friction = useMemo(() => frictionSteps(interactions), [interactions]);
	const info = (name: string) => resolveInfo(lookup, name);
	const topReach = Math.max(1, ...funnel.map((step) => step.reached));
	const topLoss = Math.max(1, ...losses.map((item) => item.stopped));
	const canFilter = (name: string) => !isGroupLabel(simplify, name);

	if (!result) {
		return <div className={styles.funnel_empty}>{loading ? 'Loading the funnel…' : 'No data yet.'}</div>;
	}
	if (interactions.length === 0) {
		return <div className={styles.funnel_empty}>No customers match these filters.</div>;
	}

	const stepName = (name: string) => (
		<span className={styles.funnel_step_name}>
			<Dot color={info(name).color} />
			{info(name).label}
		</span>
	);

	return (
		<div className={`${styles.funnel} ${loading ? styles.funnel_loading : ''}`}>
			<div className={styles.funnel_header}>
				<div>
					<div className={styles.funnel_title}>Funnel</div>
					<div className={styles.funnel_note}>
						Steps follow the route most customers take. Customers who stop at the success step are not
						counted as drop-offs.
					</div>
				</div>
				<label className={styles.funnel_goal}>
					Success step
					<select
						className={styles.funnel_select}
						value={goal ?? ''}
						onChange={(event) => onGoalChange(event.target.value || null)}
					>
						<option value="">None</option>
						{goalOptions.map((name) => (
							<option key={name} value={name}>
								{info(name).label}
							</option>
						))}
					</select>
				</label>
			</div>

			<div className={styles.funnel_insights}>
				<section className={styles.insight_card}>
					<div className={styles.insight_title}>Where customers drop off</div>
					<div className={styles.insight_note}>The last step customers reached in this period</div>
					{losses.length === 0 ? (
						<div className={styles.insight_empty}>No drop-offs outside the success step.</div>
					) : (
						<ol className={styles.insight_list}>
							{losses.map((item) => (
								<li key={item.name} className={styles.insight_row}>
									<div className={styles.insight_row_head}>
										{stepName(item.name)}
										<span className={styles.insight_value}>
											{formatCompact(item.stopped)} stopped
										</span>
									</div>
									<div className={styles.insight_bar}>
										<span style={{ width: `${(item.stopped / topLoss) * 100}%` }} />
									</div>
									<div className={styles.insight_row_foot}>
										{pctText(item.stoppedPct)} of the {formatCompact(item.reached)} who reached it
										{canFilter(item.name) && (
											<button
												type="button"
												className={styles.insight_link}
												onClick={() => onStoppedHere(item.name)}
											>
												Show these customers
											</button>
										)}
									</div>
								</li>
							))}
						</ol>
					)}
				</section>
				<section className={styles.insight_card}>
					<div className={styles.insight_title}>Friction</div>
					<div className={styles.insight_note}>Steps customers repeat, or spend unusually long on</div>
					{friction.length === 0 ? (
						<div className={styles.insight_empty}>No repeated or slow steps stand out.</div>
					) : (
						<ul className={styles.insight_list}>
							{friction.map((item) => (
								<li key={item.name} className={styles.insight_row}>
									<div className={styles.insight_row_head}>
										{stepName(item.name)}
										<span className={styles.friction_chip}>
											{item.reason === 'repeated'
												? `${item.repeatRatio.toFixed(1)}× per customer`
												: `${formatDuration(item.medianSecs)} to next step`}
										</span>
									</div>
									<div className={styles.insight_row_foot}>
										{item.reason === 'repeated'
											? 'Customers do this step more than once, often a sign of a confusing question or an error'
											: 'Customers take much longer here than at a typical step'}
									</div>
								</li>
							))}
						</ul>
					)}
				</section>
			</div>

			<table className={styles.funnel_table}>
				<thead>
					<tr>
						<th>Step</th>
						<th>Reached</th>
						<th>Went straight to the next step</th>
						<th>Stopped here</th>
						<th>Median time to next</th>
					</tr>
				</thead>
				<tbody>
					{funnel.map((step, index) => (
						<tr key={step.name}>
							<td>
								<span className={styles.funnel_index}>{index + 1}</span>
								{stepName(step.name)}
								{step.repeated && <span className={styles.friction_chip}>Repeated</span>}
								{step.slow && <span className={styles.friction_chip}>Slow</span>}
							</td>
							<td>
								<div className={styles.funnel_reach}>
									<span className={styles.funnel_reach_bar}>
										<span style={{ width: `${(step.reached / topReach) * 100}%` }} />
									</span>
									{formatCompact(step.reached)}
								</div>
							</td>
							<td>{step.continuedPct === null ? '–' : pctText(step.continuedPct)}</td>
							<td
								className={
									step.name !== goalView && step.stoppedPct >= 20 ? styles.funnel_loss : undefined
								}
							>
								{formatCompact(step.stopped)} ({pctText(step.stoppedPct)})
							</td>
							<td>{formatDuration(step.medianSecs)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export default JourneyFunnelView;
